// HarnessAgent termination modes mapping exactly to the session lifecycle
// (docs/03-harnesses/01-harness-agent.md): destroy() = end, detach() = park
// warm, stop() = park cold, suspendTurn() = cross-process handoff.
export const SESSION_TERMINATION_MODES = {
  DESTROY: "destroy",
  DETACH: "detach",
  STOP: "stop",
  SUSPEND_TURN: "suspend-turn",
} as const;

export type SessionTerminationMode =
  (typeof SESSION_TERMINATION_MODES)[keyof typeof SESSION_TERMINATION_MODES];
export const ALL_SESSION_TERMINATION_MODES = Object.values(
  SESSION_TERMINATION_MODES,
);

// Platform host tools exposed to every harness
// (docs/03-harnesses/01-harness-agent.md, host tools row).
export const HOST_TOOL_NAMES = {
  ADVISE: "advise",
  ASK_USER: "ask_user",
  SAVE_DEV_ENV_MANIFEST: "save_dev_env_manifest",
  REQUEST_DEV_ENV_CREDENTIALS: "request_dev_env_credentials",
  // Its sibling for every OTHER task type: the value is stored for the repo
  // and pushed to its Vercel project (docs/integrations/env-vars.md).
  REQUEST_ENV_VARS: "request_env_vars",
  GENERATE_DEV_ENV_SECRET: "generate_dev_env_secret",
  SET_DEV_ENV_SIZING: "set_dev_env_sizing",
  BAKE_DEV_ENV: "bake_dev_env",
  PROPOSE_PLAN: "propose_plan",
  REPORT_CONFIDENCE: "report_confidence",
  REQUEST_GUARDRAIL_APPROVAL: "request_guardrail_approval",
  // Somnium, mid-run: the automatic recall only fires on a session's FIRST
  // prompt, so the agent reads on demand and writes what it learns.
  MEMORY_SEARCH: "memory_search",
  MEMORY_WRITE: "memory_write",
  // A project's shared context (docs/agents/projects.md): every session of the
  // project reads it in its instructions and writes back what it learned.
  PROJECT_CONTEXT_READ: "project_context_read",
  PROJECT_CONTEXT_WRITE: "project_context_write",
  SKILL_SEARCH: "skill_search",
  SKILL_LOAD: "skill_load",
  SAVE_SKILL: "save_skill",
  RECORD_REVIEW_LESSON: "record_review_lesson",
  // Proposes an EDIT to a skill already in the registry: a version above the
  // skill's current one, invisible to sessions until a human approves it
  // (docs/08-skills/01-registry.md).
  PROPOSE_SKILL_EDIT: "propose_skill_edit",
  DELIVERABLE_PUBLISH: "deliverable_publish",
  // Design mode: pull a round of static bundles out of the sandbox, store them
  // as immutable versions, and hand back the public link the panel mirrors.
  PUBLISH_DESIGN: "publish_design",
  SET_LIVE_DESIGN: "set_live_design",
  SUBMIT_SPECS: "submit_specs",
  SET_BRAND_KIT: "set_brand_kit",
  PLAN_TICKETS: "plan_tickets",
  // Run engine seats (docs/agents/software-factory.md): the foundation session
  // reports its checklist, the merge-decision session settles a ticket's PR,
  // the QA session settles the verification phase.
  MARK_PROVISIONING_STEP: "mark_provisioning_step",
  DECIDE_MERGE: "decide_merge",
  DECIDE_QA: "decide_qa",
  // Advisory dynamic playbook (WS6): the agent defines its own ordered steps
  // (set_steps) and fills each step's todo checklist (set_todos). Replaces the
  // fixed 5-phase set_phase pointer; neither parks the turn, the runner park
  // logic is untouched. A step's status is derived from its todos.
  SET_STEPS: "set_steps",
  SET_TODOS: "set_todos",
  // The chat's title is generated from the FIRST prompt by the platform's
  // small model. This lets the agent correct it mid-run when the real scope
  // turns out to be something else, and only then (see the pipeline
  // templates). Refused once a human has renamed the chat; never parks.
  RENAME_SESSION: "rename_session",
  // Files an issue in Hephaistos' own Linear tracker; never parks the turn.
  REPORT_HARNESS_ISSUE: "report_harness_issue",
  REPORT_DEV_SERVER: "report_dev_server",
  RUN_VALIDATION: "run_validation",
  // Media tool (Stage 1): generate images host-side via the AI Gateway; the
  // result renders inline in the thread. Always available, never parks the
  // turn.
  GENERATE_IMAGE: "generate_image",
  // In-sandbox annotation (#43): renders the source image + an SVG overlay in
  // the sandbox's chromium and screenshots it. NEVER host-side, the previous
  // @napi-rs/canvas implementation broke the glibc Trigger.dev worker deploy.
  ANNOTATE_IMAGE: "annotate_image",
  // WS8: the admin-configured image_generator registry, the agent reads the
  // enabled {model, description} rows to pick a model for generate_image.
  GET_IMAGE_GENERATORS: "get_image_generators",
  // Capture tools (Stage 2): drive a headless chromium INSIDE the session's
  // sandbox to screenshot / record a running app URL (or a file:// the agent
  // rendered), then attach the result inline. Provision the sandbox on demand
  // (WS7), always exposed.
  TAKE_SCREENSHOT: "take_screenshot",
  RECORD_VIDEO: "record_video",
  // Interactive browser control: ONE persistent chromium per sandbox, one
  // context per session, driven by the in-sandbox daemon (packages/tasks
  // lib/browser). The snapshot is an accessibility tree with `ref=eN` handles
  // the act tool targets, no coordinates, no throwaway playwright script.
  BROWSER_NAVIGATE: "browser_navigate",
  BROWSER_SNAPSHOT: "browser_snapshot",
  BROWSER_ACT: "browser_act",
  BROWSER_WAIT: "browser_wait",
  BROWSER_INSPECT: "browser_inspect",
  BROWSER_SCREENSHOT: "browser_screenshot",
  // A recording is a PAIR: playwright only films a context it created with
  // recording on, so start recycles the context and stop hands back the clip.
  BROWSER_RECORD_START: "browser_record_start",
  BROWSER_RECORD_STOP: "browser_record_stop",
  BROWSER_CLOSE: "browser_close",
  // The escape hatch: JS evaluated in a Node context that SURVIVES between
  // calls, with the live page bound into it.
  REPL_EVAL: "repl_eval",
  REPL_RESET: "repl_reset",
  // WS5: upload a PNG the agent produced in the sandbox (script/python/write)
  // as a media reference, silent for ask_user option images, inline otherwise.
  ATTACH_IMAGE: "attach_image",
  // Browser-session vault: sites a human captured with the Hephaistos
  // extension, applied to the live browser one domain at a time, on demand.
  BROWSER_SESSIONS_LIST: "browser_sessions_list",
  BROWSER_USE_SESSION: "browser_use_session",
  BROWSER_FORGET_SESSION: "browser_forget_session",
  // Outbound files: any format the human should keep, uploaded from the sandbox
  // to durable storage and downloadable once the sandbox is gone.
  ATTACH_FILE: "attach_file",
  // Delegation: a task is the work, a session is one attempt at it. The agent
  // describes the task and the PLATFORM dispatches it; these address the task,
  // never a runner. Fire-and-forget, none of them park the turn.
  CREATE_TASK: "create_task",
  LIST_TASKS: "list_tasks",
  UPDATE_TASK: "update_task",
  PEEK_TASK: "peek_task",
  SEND_TO_TASK: "send_to_task",
  STOP_TASK: "stop_task",
  PICKUP_TASK: "pickup_task",
  // A JS script whose agent() calls ARE platform tasks (docs/agents/workflows.md).
  WORKFLOW: "workflow",
  // docs/integrations/database-access.md; detect is environment-setup only.
  DATABASE_QUERY: "database_query",
  DATABASE_WRITE: "database_write",
  DETECT_PROJECT_DATABASE: "detect_project_database",
  BIND_PROJECT_DATABASE: "bind_project_database",
  // Historical: the agent-addressed family the tools above replaced. No agent
  // can call them, but old transcripts still render rows named after them.
  SPAWN_AGENT: "spawn_agent",
  LIST_AGENTS: "list_agents",
  PEEK_AGENT: "peek_agent",
  SEND_TO_AGENT: "send_to_agent",
  STOP_AGENT: "stop_agent",
} as const;

export type HostToolName =
  (typeof HOST_TOOL_NAMES)[keyof typeof HOST_TOOL_NAMES];
export const ALL_HOST_TOOL_NAMES = Object.values(HOST_TOOL_NAMES);

export const TURN_CLOSING_TOOL_NAMES: readonly HostToolName[] = [
  HOST_TOOL_NAMES.UPDATE_TASK,
  HOST_TOOL_NAMES.CREATE_TASK,
];

// The file and shell tools the worker runs against the session VM
// (@repo/tasks `remoteWorkspaceTools`). They shadow the harness' native
// builtins field for field, so anything reading a tool call by name, the
// transcript, the chat mirrors, must map them back onto the native family.
export const WORKSPACE_TOOL_NAMES = {
  BASH: "workspace_bash",
  READ: "workspace_read",
  WRITE: "workspace_write",
  EDIT: "workspace_edit",
  GLOB: "workspace_glob",
  GREP: "workspace_grep",
  TASK_OUTPUT: "workspace_task_output",
  TASK_STOP: "workspace_task_stop",
} as const;

export type WorkspaceToolName =
  (typeof WORKSPACE_TOOL_NAMES)[keyof typeof WORKSPACE_TOOL_NAMES];

/**
 * MCP namespace the bridge registers the platform's own toolbox under. Only
 * this namespace may be dropped when matching a tool call by name: a
 * third-party server is free to expose its own `read` or `create_task`, and
 * stripping its namespace too would let it borrow a built-in's label.
 */
export const HARNESS_MCP_NAMESPACE = "mcp__harness-tools__";

// How a `send_to_task` message reaches an attempt that is already working:
// abort the current turn immediately, stop it at the next settled tool call,
// or wait for its next turn.
export const AGENT_MESSAGE_DELIVERY_MODES = {
  INTERRUPT: "interrupt",
  AFTER_TOOL: "after_tool",
  QUEUE: "queue",
} as const;

export type AgentMessageDeliveryMode =
  (typeof AGENT_MESSAGE_DELIVERY_MODES)[keyof typeof AGENT_MESSAGE_DELIVERY_MODES];
export const ALL_AGENT_MESSAGE_DELIVERY_MODES = Object.values(
  AGENT_MESSAGE_DELIVERY_MODES,
);
export const DEFAULT_AGENT_MESSAGE_DELIVERY_MODE =
  AGENT_MESSAGE_DELIVERY_MODES.AFTER_TOOL;
export const PARKED_TOOL_CALL_OUTPUT =
  "The session parked on this call, the turn ended here.";
/** `payload.source` of a steering row written by a parent session. */
export const AGENT_MESSAGE_SOURCE_PARENT_SESSION = "parent_session";
/** `payload.source` of a steering row a child session wrote to its parent. */
export const AGENT_MESSAGE_SOURCE_CHILD_SESSION = "child_session";
/** `payload.source` of the row the platform writes when a sandbox dies mid-turn. */
export const AGENT_MESSAGE_SOURCE_WORKSPACE_LOSS = "workspace_loss";
/** `payload.source` of the reminder an orchestrator gets for editing files itself. */
export const AGENT_MESSAGE_SOURCE_ORCHESTRATOR_DRIFT = "orchestrator_drift";

// Sources allowed to cut a RUNNING turn at its next settled tool call: the
// orchestration link (both directions) and the drift reminder; the rest waits.
export const AFTER_TOOL_STEERING_SOURCES = [
  AGENT_MESSAGE_SOURCE_PARENT_SESSION,
  AGENT_MESSAGE_SOURCE_CHILD_SESSION,
  AGENT_MESSAGE_SOURCE_ORCHESTRATOR_DRIFT,
] as const;
